import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BadgeVariants = 'interactive' | 'solid' | 'outline' | 'subtle';

/**
 * Represents a single selectable option.
 *
 * - `label`        — text shown inside the dropdown list item
 * - `value`        — the string stored in `value[]`
 * - `displayLabel` — optional text shown as the chip in the trigger button
 *                    instead of `label`. Falls back to `label` if omitted.
 */
export interface MultiSelectOption {
  label: string;
  value: string;
  displayLabel?: string;
}

interface ComposableMultiSelectProps {
  /** Full list of selectable options. */
  options: MultiSelectOption[];

  /** Currently selected values. */
  value: string[];

  /** Called whenever the selection changes. */
  onChange: (value: string[]) => void;

  /** Placeholder text shown when nothing is selected. */
  placeholder?: string;

  /** Badge variant used for selected item chips. Defaults to 'interactive'. */
  variant?: BadgeVariants;

  /**
   * Custom renderer for each option row inside the dropdown.
   *
   * Replaces the default Checkbox + label row entirely.
   * The `isSelected` flag reflects the current check state.
   *
   * @example
   * renderOption={({ option, isSelected }) => (
   *   <Flex justify="space-between" w="full">
   *     <Checkbox isChecked={isSelected}>{option.label}</Checkbox>
   *     <Badge>{option.value}</Badge>
   *   </Flex>
   * )}
   */
  renderOption?: (context: {
    option: MultiSelectOption;
    isSelected: boolean;
  }) => React.ReactNode;

  /**
   * Content for the sticky action button at the bottom of the list.
   * When provided, replaces the default "Clear All" button.
   */
  customButtonText?: string | React.ReactNode;

  /** Hide the default "Clear All" sticky button. */
  hideClearButton?: boolean;

  /** Called when the custom sticky button is clicked. */
  customButtonOnClickFunction?: () => void;

  /** Prepend a "Select All / Deselect All" row to the option list. */
  showSelectAllOption?: boolean;

  /** Label for the select-all row. Defaults to 'Select All'. */
  selectAllLabel?: string;

  /** Maximum height of the dropdown list. Defaults to '200px'. */
  maxMenuHeight?: string;

  /** Whether the component is disabled. */
  isDisabled?: boolean;
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

const StickyActionRow: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    position="sticky"
    bottom="0"
    zIndex="docked"
    borderTop="1px solid"
    borderColor="borderSubtle"
  >
    {children}
  </Box>
);

const ChakraFlexibleMultiSelect: React.FC<ComposableMultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select',
  variant = 'interactive',
  renderOption,
  customButtonText,
  hideClearButton,
  customButtonOnClickFunction,
  showSelectAllOption = false,
  selectAllLabel = 'Select All',
  maxMenuHeight = '200px',
  isDisabled = false
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

  // ── Selection helpers ──────────────────────────────────────────────────────

  const handleToggle = (val: string) => {
    onChange(
      value.includes(val) ? value.filter(v => v !== val) : [...value, val]
    );
  };

  const handleClear = () => onChange([]);

  const allOptionValues = options.map(o => o.value);
  const areAllSelected =
    options.length > 0 && allOptionValues.every(v => value.includes(v));

  const handleSelectAllToggle = () => {
    onChange(areAllSelected ? [] : allOptionValues);
  };

  // ── Sync menu width to button width ───────────────────────────────────────

  useEffect(() => {
    if (buttonRef.current) {
      setMenuWidth(buttonRef.current.getBoundingClientRect().width);
    }
  }, []);

  // ── Selected options (preserving option order) ─────────────────────────────

  const selectedOptions = options.filter(o => value.includes(o.value));

  // ── Chip renderer ──────────────────────────────────────────────────────────

  const renderChip = (option: MultiSelectOption) => (
    <Badge variant={variant} isTruncated>
      {option.displayLabel ?? option.label}
    </Badge>
  );

  // ── Option row renderer ────────────────────────────────────────────────────

  const renderOptionRow = (option: MultiSelectOption, isSelected: boolean) => {
    if (renderOption) {
      return renderOption({ option, isSelected });
    }

    return (
      <Box pointerEvents="none">
        <Checkbox
          isChecked={isSelected}
          aria-label={option.value}
          id={option.value}
          name={option.label}
        >
          {option.label}
        </Checkbox>
      </Box>
    );
  };

  return (
    <Menu closeOnSelect={false}>
      <MenuButton
        as={Button}
        rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
        width="100%"
        textAlign="left"
        backgroundColor="surfacePrimaryFill"
        border="1px solid"
        _focus={{ boxShadow: 'outline', bg: 'surfacePrimaryFill' }}
        _hover={{ bg: 'surfacePrimaryFill' }}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        ref={buttonRef}
        height="auto"
        py={2}
        px={2}
        isDisabled={isDisabled}
      >
        <Flex flexWrap="wrap" gap={2}>
          {selectedOptions.length === 0 ? (
            <Text textStyle="textBody">{placeholder}</Text>
          ) : (
            selectedOptions.map(option => (
              <React.Fragment key={option.value}>
                {renderChip(option)}
              </React.Fragment>
            ))
          )}
        </Flex>
      </MenuButton>

      <MenuList
        maxHeight={maxMenuHeight}
        overflowY="auto"
        width={menuWidth ? `${menuWidth}px` : 'auto'}
        py={0}
      >
        {/* ── Select All row ──────────────────────────────────────── */}
        {showSelectAllOption && options.length > 0 && (
          <MenuItem
            closeOnSelect={false}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleSelectAllToggle();
            }}
          >
            <Box pointerEvents="none">
              <Checkbox
                isChecked={areAllSelected}
                aria-label={selectAllLabel}
                id="select-all"
                name={selectAllLabel}
              >
                {selectAllLabel}
              </Checkbox>
            </Box>
          </MenuItem>
        )}

        {/* ── Option rows ─────────────────────────────────────────── */}
        {options.map(option => (
          <MenuItem
            key={option.value}
            closeOnSelect={false}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleToggle(option.value);
            }}
          >
            {renderOptionRow(option, value.includes(option.value))}
          </MenuItem>
        ))}

        {/* ── Sticky action button ─────────────────────────────────── */}
        {!customButtonText ? (
          !hideClearButton && (
            <StickyActionRow>
              <MenuItem onClick={handleClear}>
                <Text color="actionSecondaryCautionContent">Clear All</Text>
              </MenuItem>
            </StickyActionRow>
          )
        ) : (
          <StickyActionRow>
            <MenuItem onClick={customButtonOnClickFunction}>
              <Text color="actionSecondaryContent">{customButtonText}</Text>
            </MenuItem>
          </StickyActionRow>
        )}
      </MenuList>
    </Menu>
  );
};

export default ChakraFlexibleMultiSelect;